import type { SkillChoiceDto, SkillRepositoryDto, SkillSourceDto } from '@dbrain/shared';
import { Icons } from '@dbrain/ui';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { formatSize } from './SkillParts.js';
import { sentenceSize } from './source-error.js';

/**
 * Phase 16c.3: a GitHub repository in "Skill ekle". A big repository is no
 * error any more: the note says how big it is and that only the skill's
 * folder is downloaded (SkillGitHubBuyukDepo.dc.html). Several skills are
 * chosen with checkboxes; one over the limits is locked with its reason and
 * the others can still be installed; only the chosen folders are downloaded
 * (SkillGitHubCoklu.dc.html). Sizes in the interface's units (1 MB = 1024 KB,
 * as the 20 MB limit is counted).
 */
export function RepoNote({ repository, name, files, bytes }: { repository: SkillRepositoryDto; name: string; files: number; bytes: number }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  return (
    <div className="skill-note" role="note" data-testid="skill-repo-note">
      <Icons.workspace size={17} className="skill-note__icon" />
      <div className="skill-note__text">
        <p className="skill-note__lead">
          <Trans i18nKey="skills.github.foundOne" values={{ name, files: files.toLocaleString(locale), size: formatSize(bytes) }} components={{ b: <strong /> }} />
        </p>
        <p className="skill-note__small">
          {repository.truncated
            ? t('skills.github.wholeCut')
            : t('skills.github.whole', { files: repository.files.toLocaleString(locale), size: sentenceSize(repository.bytes, locale) })}
        </p>
      </div>
    </div>
  );
}

function overText(choice: SkillChoiceDto, limits: SkillSourceDto['limits'], t: (key: string, options?: Record<string, unknown>) => string, locale: string) {
  const reason = choice.over?.reason ?? 'bytes';
  const size = reason === 'file' ? (choice.over?.size ?? 0) : choice.bytes;
  return {
    label: t(`skills.github.over.${reason}`, { size: sentenceSize(size, locale), files: choice.files.toLocaleString(locale) }),
    hint: t(`skills.github.overHint.${reason}`, {
      limit: reason === 'files' ? limits.files.toLocaleString(locale) : sentenceSize(reason === 'file' ? limits.fileBytes : limits.bytes, locale),
    }),
  };
}

export function SkillChooser({ source, selected, onToggle }: { source: SkillSourceDto; selected: ReadonlySet<string>; onToggle: (folder: string) => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const legendId = useId();
  const over = source.choices.filter((c) => c.over).length;
  const fits = source.choices.length - over;
  return (
    <div className="skill-chooser" data-testid="skill-chooser">
      <div className="skill-note" role="note">
        <Icons.workspace size={17} className="skill-note__icon" />
        <div className="skill-note__text">
          <p className="skill-note__lead">
            <Trans i18nKey="skills.github.found" values={{ count: source.choices.length }} components={{ b: <strong /> }} />
          </p>
          <p className="skill-note__small">
            {t('skills.github.onlyChosen')}
            {over > 0 ? ` ${fits === 0 ? t('skills.github.allOver') : over === 1 ? t('skills.github.oneOver') : t('skills.github.someOver', { count: over })}` : ''}
          </p>
        </div>
      </div>
      <fieldset className="skill-choices" aria-labelledby={legendId}>
        <div className="skill-choices__head">
          <span id={legendId} className="ui-field__label">
            {t('skills.github.which')}
          </span>
          <span className="skill-choices__count" data-testid="skill-chooser-count">
            {t('skills.github.selected', { count: selected.size })}
          </span>
        </div>
        {source.choices.map((choice) => {
          const locked = choice.over ? overText(choice, source.limits, t, locale) : null;
          const checked = !locked && selected.has(choice.folder);
          return (
            <label
              key={choice.folder}
              className={`skill-choice${checked ? ' skill-choice--checked' : ''}${locked ? ' skill-choice--locked' : ''}`}
              data-testid="skill-choice"
              data-folder={choice.folder}
              data-locked={locked ? 'true' : undefined}
            >
              <input type="checkbox" checked={checked} disabled={Boolean(locked)} onChange={() => onToggle(choice.folder)} />
              <span className="skill-choice__titles">
                <span className="skill-choice__name">{choice.name}</span>
                <code className="skill-choice__folder">{choice.folder || '/'}</code>
              </span>
              {locked ? (
                <span className="skill-choice__over">
                  <span className="skill-choice__reason">
                    <Icons.error size={13} />
                    {locked.label}
                  </span>
                  <span className="skill-choice__hint">{locked.hint}</span>
                </span>
              ) : (
                <span className="skill-choice__meta">
                  {t(choice.scripts > 0 ? 'skills.github.metaScripts' : 'skills.github.meta', { files: choice.files.toLocaleString(locale), size: formatSize(choice.bytes), scripts: choice.scripts })}
                </span>
              )}
            </label>
          );
        })}
      </fieldset>
    </div>
  );
}
